import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  ScatterChart, Scatter, ZAxis, ReferenceLine, ReferenceArea, LabelList,
} from "recharts";
import { Sparkles, Loader2, Save, Trash2, Plus, ArrowRight, TrendingUp, BarChart3, Grid3X3 } from "lucide-react";

const MATRIX_PRESETS = [
  {
    key: "valeur_volume",
    label: "💰 Valeur × Volume",
    desc: "Où est la valeur ?",
    xLabel: "Valeur annuelle par client (CHF/an) →",
    yLabel: "Part de la clientèle →",
    xFmt: (v) => v.toLocaleString("fr-CH"),
    yFmt: (v) => `${v}%`,
    getX: (r) => Math.round((r.mrr_chf * 12 + r.ca_ponctuel_chf) / r.nb_clients),
    getY: (r) => r.part_clients_pct,
    target: "tr",
    tooltip: (p) => `${p.x.toLocaleString("fr-CH")} CHF/an par client`,
  },
  {
    key: "prix_volume",
    label: "🏷️ Échelle tarifaire",
    desc: "Votre échelle de prix est-elle cohérente ?",
    xLabel: "Prix affiché de l'offre (CHF) →",
    yLabel: "Nombre de clients →",
    xFmt: (v) => v.toLocaleString("fr-CH"),
    yFmt: (v) => v,
    getX: (r) => r.prix_chf,
    getY: (r) => r.nb_clients,
    target: "tr",
    tooltip: (p) => `Prix : ${p.x.toLocaleString("fr-CH")} CHF`,
  },
  {
    key: "effort_rendement",
    label: "⏱️ Effort × Rendement",
    desc: "Qu'est-ce qui paie votre temps ?",
    xLabel: "Effort par client (h/mois) →",
    yLabel: "Valeur annuelle par client (CHF/an) →",
    xFmt: (v) => `${v}h`,
    yFmt: (v) => v.toLocaleString("fr-CH"),
    getX: (r) => r.effort_h_mois || 0,
    getY: (r) => Math.round((r.mrr_chf * 12 + r.ca_ponctuel_chf) / r.nb_clients),
    target: "tl",
    requires: "effort_h_mois",
    requiresHint: "Renseignez la colonne « Effort h/mois » dans le tableau des offres pour activer cette matrice.",
    tooltip: (p) => `${p.x}h/mois · ${p.y.toLocaleString("fr-CH")} CHF/an`,
  },
  {
    key: "engagement_valeur",
    label: "📅 Engagement × Valeur",
    desc: "La fidélisation paie-t-elle ?",
    xLabel: "Durée d'engagement (mois) →",
    yLabel: "Valeur annuelle par client (CHF/an) →",
    xFmt: (v) => `${v} mois`,
    yFmt: (v) => v.toLocaleString("fr-CH"),
    getX: (r) => r.duree_mois || 0,
    getY: (r) => Math.round((r.mrr_chf * 12 + r.ca_ponctuel_chf) / r.nb_clients),
    target: "tr",
    requires: "duree_mois",
    requiresHint: "Renseignez la colonne « Durée (mois) » dans le tableau des offres pour activer cette matrice.",
    tooltip: (p) => `${p.x} mois · ${p.y.toLocaleString("fr-CH")} CHF/an`,
  },
];

const PERIODICITES = [
  { v: "ponctuel", label: "Pack ponctuel" },
  { v: "mensuel", label: "Abo mensuel" },
  { v: "trimestriel", label: "Abo trimestriel" },
  { v: "semestriel", label: "Abo semestriel" },
  { v: "annuel", label: "Abo annuel" },
];

const chf = (v) => `${Number(v || 0).toLocaleString("fr-CH")} CHF`;

export default function DispersionClientele() {
  const [offres, setOffres] = useState([]);
  const [stats, setStats] = useState(null);
  const [lecture, setLecture] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(false);
  const [sim, setSim] = useState({ from_id: "", to_id: "", nb: 1 });
  const [simResult, setSimResult] = useState(null);
  const [simBusy, setSimBusy] = useState(false);
  const [chartMode, setChartMode] = useState("matrix");
  const [matrixKey, setMatrixKey] = useState("valeur_volume");

  const load = async () => {
    try {
      const r = await api.get("/workspace/asymetries/dispersion/clientele");
      setOffres(r.data.offres || []);
      setStats(r.data.stats);
      setLecture(r.data.lecture || "");
    } catch (e) {
      notify.error(fmtError(e, "Chargement impossible."));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/workspace/asymetries/dispersion/clientele/offres", { offres });
      setOffres(r.data.offres);
      setStats(r.data.stats);
      setSimResult(null);
      notify.success("Mix d'offres enregistré.");
    } catch (e) {
      notify.error(fmtError(e, "Sauvegarde impossible."));
    } finally { setSaving(false); }
  };

  const runLecture = async () => {
    setReading(true);
    try {
      const r = await api.post("/workspace/asymetries/dispersion/clientele/lecture");
      setLecture(r.data.lecture);
    } catch (e) {
      notify.error(fmtError(e, "Lecture IA impossible."));
    } finally { setReading(false); }
  };

  const simulate = async () => {
    if (!sim.from_id || !sim.to_id || sim.from_id === sim.to_id) { notify.error("Choisissez une offre source et une offre cible différentes."); return; }
    setSimBusy(true);
    try {
      const r = await api.post("/workspace/asymetries/dispersion/clientele/simulate", sim);
      setSimResult(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Simulation impossible."));
    } finally { setSimBusy(false); }
  };

  const updateOffre = (i, k, v) => setOffres((arr) => arr.map((o, idx) => idx === i ? { ...o, [k]: v } : o));

  if (loading) return <div className="flex items-center justify-center py-16 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…</div>;

  const rows = stats?.offres || [];

  return (
    <div className="space-y-4" data-testid="dispersion-clientele">
      {/* KPIs */}
      {stats && stats.total_clients > 0 && (
        <div className="grid md:grid-cols-5 gap-3" data-testid="clientele-kpis">
          <Kpi label="Clients" value={stats.total_clients} />
          <Kpi label="MRR (récurrent/mois)" value={chf(stats.mrr_chf)} accent="#1D9E75" />
          <Kpi label="ARR (annualisé)" value={chf(stats.arr_chf)} accent="#1D9E75" />
          <Kpi label="Taux de récurrence" value={`${stats.taux_recurrence_pct}%`} accent={stats.taux_recurrence_pct < 50 ? "#EF9F27" : "#1D9E75"} hint={`${stats.clients_ponctuels} client(s) ponctuel(s)`} />
          {stats.concentration_top && (
            <Kpi label="Concentration max" value={`${stats.concentration_top.pct}%`} accent={stats.concentration_top.pct > 40 ? "#EF4444" : "#71717A"} hint={stats.concentration_top.nom} />
          )}
        </div>
      )}

      {/* Répartition clients */}
      {rows.length > 0 && (
        <div className="noam-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="noam-overline">
              {chartMode === "matrix" ? "Matrice de dispersion — Valeur par client × Part de clientèle" : "Répartition des clients par offre"}
            </div>
            <div className="flex items-center rounded-md border border-white/10 overflow-hidden">
              <button type="button" onClick={() => setChartMode("matrix")}
                className={`px-2.5 py-1 text-[11px] font-semibold inline-flex items-center gap-1 transition-colors ${chartMode === "matrix" ? "bg-[#378ADD]/15 text-[#378ADD]" : "text-zinc-500 hover:text-zinc-300"}`}
                data-testid="chart-mode-matrix">
                <Grid3X3 className="w-3 h-3" /> Matrice
              </button>
              <button type="button" onClick={() => setChartMode("bars")}
                className={`px-2.5 py-1 text-[11px] font-semibold inline-flex items-center gap-1 transition-colors ${chartMode === "bars" ? "bg-[#378ADD]/15 text-[#378ADD]" : "text-zinc-500 hover:text-zinc-300"}`}
                data-testid="chart-mode-bars">
                <BarChart3 className="w-3 h-3" /> Barres
              </button>
            </div>
          </div>
          {chartMode === "matrix" ? (
            <>
              <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="matrix-presets">
                {MATRIX_PRESETS.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMatrixKey(m.key)}
                    title={m.desc}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${matrixKey === m.key ? "bg-[#378ADD]/15 text-[#378ADD] border-[#378ADD]/40" : "text-zinc-400 border-white/10 hover:text-zinc-200"}`}
                    data-testid={`matrix-preset-${m.key}`}
                  >
                    {m.label}
                  </button>
                ))}
                <span className="text-[10px] text-zinc-500 italic ml-2">{MATRIX_PRESETS.find((m) => m.key === matrixKey)?.desc}</span>
              </div>
              <DispersionMatrix rows={rows} preset={MATRIX_PRESETS.find((m) => m.key === matrixKey)} />
            </>
          ) : (
          <>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={rows} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="nom" tick={{ fill: "#71717A", fontSize: 9 }} interval={0} angle={-12} height={45} />
              <YAxis tick={{ fill: "#71717A", fontSize: 10 }} allowDecimals={false} />
              <Tooltip content={({ payload }) => payload?.length ? (
                <div className="bg-black/90 border border-white/20 rounded px-3 py-2 text-xs">
                  <div className="font-bold text-white">{payload[0].payload.nom}</div>
                  <div className="text-zinc-400">{payload[0].payload.nb_clients} clients ({payload[0].payload.part_clients_pct}%) · {chf(payload[0].payload.prix_chf)}</div>
                  <div className="text-emerald-400">MRR : {chf(payload[0].payload.mrr_chf)}</div>
                </div>
              ) : null} />
              <Bar dataKey="nb_clients" radius={[3, 3, 0, 0]}>
                {rows.map((r, i) => (
                  <Cell key={i} fill={r.periodicite === "ponctuel" ? "#EF9F27" : "#1D9E75"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex gap-4 text-[10px] text-zinc-500 mt-1">
            <span><span className="inline-block w-2 h-2 rounded-sm bg-[#EF9F27] mr-1" />Packs ponctuels</span>
            <span><span className="inline-block w-2 h-2 rounded-sm bg-[#1D9E75] mr-1" />Abonnements récurrents</span>
          </div>
          </>
          )}
        </div>
      )}

      {/* Simulateur de migration */}
      {offres.length >= 2 && (
        <div className="noam-card p-4 border-[#378ADD]/20 bg-[#378ADD]/[0.03]" data-testid="migration-simulator">
          <div className="noam-overline text-[#378ADD] mb-3 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Simulateur de migration</div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-zinc-400">Déplacer</span>
            <input type="number" min={1} value={sim.nb} onChange={(e) => setSim((s) => ({ ...s, nb: Number(e.target.value) }))} className="noam-input text-xs h-8 w-20" data-testid="sim-nb-input" />
            <span className="text-xs text-zinc-400">client(s) de</span>
            <select value={sim.from_id} onChange={(e) => setSim((s) => ({ ...s, from_id: e.target.value }))} className="noam-input text-xs h-8 w-48" data-testid="sim-from-select">
              <option value="">— offre source —</option>
              {offres.map((o) => <option key={o.id} value={o.id}>{o.nom} ({o.nb_clients})</option>)}
            </select>
            <ArrowRight className="w-3.5 h-3.5 text-zinc-500" />
            <select value={sim.to_id} onChange={(e) => setSim((s) => ({ ...s, to_id: e.target.value }))} className="noam-input text-xs h-8 w-48" data-testid="sim-to-select">
              <option value="">— offre cible —</option>
              {offres.map((o) => <option key={o.id} value={o.id}>{o.nom}</option>)}
            </select>
            <button onClick={simulate} disabled={simBusy} className="btn-primary text-xs disabled:opacity-50" data-testid="sim-run-btn">
              {simBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />} Simuler
            </button>
          </div>
          {simResult && (
            <div className="grid md:grid-cols-3 gap-3 mt-4" data-testid="sim-result">
              <div className="p-3 rounded bg-white/[0.03] border border-white/5">
                <div className="text-[10px] uppercase text-zinc-500">Δ MRR</div>
                <div className={`text-xl font-bold ${simResult.delta_mrr_chf >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {simResult.delta_mrr_chf >= 0 ? "+" : ""}{chf(simResult.delta_mrr_chf)}/mois
                </div>
              </div>
              <div className="p-3 rounded bg-white/[0.03] border border-white/5">
                <div className="text-[10px] uppercase text-zinc-500">Δ ARR (annualisé)</div>
                <div className={`text-xl font-bold ${simResult.delta_arr_chf >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {simResult.delta_arr_chf >= 0 ? "+" : ""}{chf(simResult.delta_arr_chf)}/an
                </div>
              </div>
              <div className="p-3 rounded bg-white/[0.03] border border-white/5">
                <div className="text-[10px] uppercase text-zinc-500">Récurrence {simResult.before.taux_recurrence_pct}% → {simResult.after.taux_recurrence_pct}%</div>
                <div className="text-xs text-zinc-400 mt-1">
                  CA ponctuel : {simResult.delta_ca_ponctuel_chf >= 0 ? "+" : ""}{chf(simResult.delta_ca_ponctuel_chf)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lecture IA */}
      <div className="flex items-center justify-between">
        <div className="noam-overline">Mix d'offres ({offres.length})</div>
        <div className="flex gap-2">
          {offres.length > 0 && (
            <button onClick={runLecture} disabled={reading} className="btn-ghost text-xs disabled:opacity-50" data-testid="clientele-lecture-btn">
              {reading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Lecture IA
            </button>
          )}
          <button onClick={() => setOffres((a) => [...a, { nom: "", categorie: "", prix_chf: 0, periodicite: "ponctuel", nb_clients: 0 }])} className="btn-ghost text-xs" data-testid="offre-add-btn">
            <Plus className="w-3 h-3" /> Ajouter une offre
          </button>
          <button onClick={save} disabled={saving} className="btn-primary text-xs disabled:opacity-50" data-testid="offres-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Enregistrer
          </button>
        </div>
      </div>

      {lecture && (
        <div className="noam-card p-5 border-[#EF9F27]/20" data-testid="clientele-lecture">
          <div className="noam-overline text-[#EF9F27] mb-2">Lecture des asymétries</div>
          <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{lecture}</p>
        </div>
      )}

      {offres.length === 0 ? (
        <div className="noam-card p-10 text-center text-sm text-zinc-500" data-testid="clientele-empty">
          Saisissez vos offres (nom, prix, périodicité, nombre de clients) pour lire la dispersion
          de votre clientèle et simuler des migrations pack → abonnement.
        </div>
      ) : (
        <div className="noam-card p-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-zinc-500 text-left border-b border-white/10">
                <th className="py-2 pr-2">Offre</th><th className="pr-2">Catégorie</th>
                <th className="pr-2 w-28">Prix CHF</th><th className="pr-2 w-36">Périodicité</th>
                <th className="pr-2 w-24">Nb clients</th>
                <th className="pr-2 w-24" title="Heures de travail par client et par mois — active la matrice Effort × Rendement">Effort h/mois</th>
                <th className="pr-2 w-24" title="Durée d'engagement en mois — active la matrice Engagement × Valeur">Durée (mois)</th>
                <th className="pr-2 w-28">MRR</th><th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {offres.map((o, i) => {
                const row = rows.find((r) => r.id === o.id);
                return (
                  <tr key={o.id || i} className="border-b border-white/5" data-testid={`offre-row-${i}`}>
                    <td className="py-1.5 pr-2"><input value={o.nom} onChange={(e) => updateOffre(i, "nom", e.target.value)} placeholder="Ex : Pack 10 séances" className="noam-input text-xs h-7 w-full min-w-[140px]" /></td>
                    <td className="pr-2"><input value={o.categorie} onChange={(e) => updateOffre(i, "categorie", e.target.value)} placeholder="Ex : Coaching collectif" className="noam-input text-xs h-7 w-full min-w-[110px]" /></td>
                    <td className="pr-2"><input type="number" min={0} value={o.prix_chf} onChange={(e) => updateOffre(i, "prix_chf", Number(e.target.value))} className="noam-input text-xs h-7 w-24" /></td>
                    <td className="pr-2">
                      <select value={o.periodicite} onChange={(e) => updateOffre(i, "periodicite", e.target.value)} className="noam-input text-xs h-7 w-32">
                        {PERIODICITES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                      </select>
                    </td>
                    <td className="pr-2"><input type="number" min={0} value={o.nb_clients} onChange={(e) => updateOffre(i, "nb_clients", Number(e.target.value))} className="noam-input text-xs h-7 w-20" /></td>
                    <td className="pr-2"><input type="number" min={0} step={0.5} value={o.effort_h_mois || 0} onChange={(e) => updateOffre(i, "effort_h_mois", Number(e.target.value))} className="noam-input text-xs h-7 w-20" /></td>
                    <td className="pr-2"><input type="number" min={0} value={o.duree_mois || 0} onChange={(e) => updateOffre(i, "duree_mois", Number(e.target.value))} className="noam-input text-xs h-7 w-20" /></td>
                    <td className="pr-2 text-emerald-400 font-mono">{row ? chf(row.mrr_chf) : "—"}</td>
                    <td><button onClick={() => setOffres((a) => a.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const QUADRANT_LABELS = {
  valeur_volume: { tl: "Volume fragile — à faire monter en valeur", br: "Pépites — à développer" },
  prix_volume: { tl: "Entrée de gamme surpeuplée", br: "Premium confidentiel — à pousser" },
  effort_rendement: { tr: "Rentable mais chronophage", bl: "Faible enjeu" },
  engagement_valeur: { tl: "Fidèles à faible valeur", br: "Forte valeur volatile — à engager" },
};

function DispersionMatrix({ rows, preset }) {
  const p = preset || MATRIX_PRESETS[0];
  const source = rows.filter((r) => r.nb_clients > 0 && (!p.requires || (r[p.requires] || 0) > 0));
  const pts = source.map((r) => ({
    name: r.nom,
    x: p.getX(r),
    y: p.getY(r),
    z: r.nb_clients,
    recurrent: r.periodicite !== "ponctuel",
    prix: r.prix_chf,
  }));
  if (!pts.length) {
    return (
      <div className="py-12 text-center text-xs text-zinc-500" data-testid="matrix-requires-hint">
        {p.requiresHint || "Aucune offre avec des clients."}
      </div>
    );
  }
  const maxX = Math.max(...pts.map((d) => d.x)) * 1.15 || 1;
  const maxY = Math.max(...pts.map((d) => d.y)) * 1.25 || 1;
  const maxZ = Math.max(...pts.map((d) => d.z));
  const sortedX = [...pts.map((d) => d.x)].sort((a, b) => a - b);
  const sortedY = [...pts.map((d) => d.y)].sort((a, b) => a - b);
  const medX = sortedX[Math.floor(sortedX.length / 2)];
  const medY = sortedY[Math.floor(sortedY.length / 2)];

  const CORNERS = {
    tl: { x1: 0, x2: medX, y1: medY, y2: maxY, pos: "insideTopLeft" },
    tr: { x1: medX, x2: maxX, y1: medY, y2: maxY, pos: "insideTopRight" },
    bl: { x1: 0, x2: medX, y1: 0, y2: medY, pos: "insideBottomLeft" },
    br: { x1: medX, x2: maxX, y1: 0, y2: medY, pos: "insideBottomRight" },
  };
  const target = CORNERS[p.target] || CORNERS.tr;
  const others = QUADRANT_LABELS[p.key] || {};

  const Bubble = (props) => {
    const { cx, cy, payload } = props;
    const r = 8 + 22 * Math.sqrt(payload.z / maxZ);
    const color = payload.recurrent ? "#1D9E75" : "#EF9F27";
    return (
      <g>
        <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={0.28} stroke={color} strokeWidth={1.5} />
        <text x={cx} y={cy - r - 4} textAnchor="middle" fill="#E4E4E7" fontSize={Math.min(11, 8 + 4 * (payload.z / maxZ))} fontWeight={600}>
          {payload.name}
        </text>
        <text x={cx} y={cy + 3.5} textAnchor="middle" fill={color} fontSize={10} fontWeight={700}>
          {payload.z}
        </text>
      </g>
    );
  };

  return (
    <div data-testid="dispersion-matrix">
      <ResponsiveContainer width="100%" height={380}>
        <ScatterChart margin={{ top: 25, right: 30, bottom: 15, left: 10 }}>
          <CartesianGrid stroke="#ffffff08" />
          <ReferenceArea x1={target.x1} x2={target.x2} y1={target.y1} y2={target.y2} fill="#1D9E75" fillOpacity={0.07}
            label={{ value: "★ ZONE RECHERCHÉE", position: target.pos, fill: "#1D9E75", fontSize: 10, fontWeight: 700 }} />
          {Object.entries(others).map(([corner, label]) => {
            const c = CORNERS[corner];
            return (
              <ReferenceArea key={corner} x1={c.x1} x2={c.x2} y1={c.y1} y2={c.y2} fill="#ffffff" fillOpacity={0.015}
                label={{ value: label, position: c.pos, fill: "#71717Acc", fontSize: 9 }} />
            );
          })}
          <ReferenceLine x={medX} stroke="#ffffff20" strokeDasharray="4 4" />
          <ReferenceLine y={medY} stroke="#ffffff20" strokeDasharray="4 4" />
          <XAxis type="number" dataKey="x" domain={[0, maxX]} tick={{ fill: "#71717A", fontSize: 10 }}
            tickFormatter={p.xFmt}
            label={{ value: p.xLabel, fill: "#71717A", fontSize: 10, position: "insideBottom", offset: -8 }} />
          <YAxis type="number" dataKey="y" domain={[0, maxY]} tick={{ fill: "#71717A", fontSize: 10 }}
            tickFormatter={p.yFmt}
            label={{ value: p.yLabel, fill: "#71717A", fontSize: 10, angle: -90, position: "insideLeft" }} />
          <ZAxis type="number" dataKey="z" range={[100, 900]} />
          <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => payload?.length ? (
            <div className="bg-black/90 border border-white/20 rounded px-3 py-2 text-xs">
              <div className="font-bold text-white">{payload[0].payload.name}</div>
              <div className="text-zinc-400">{payload[0].payload.z} clients · {payload[0].payload.prix} CHF</div>
              <div className="text-emerald-400">{p.tooltip(payload[0].payload)}</div>
            </div>
          ) : null} />
          <Scatter data={pts} shape={<Bubble />} />
        </ScatterChart>
      </ResponsiveContainer>
      <div className="flex gap-4 text-[10px] text-zinc-500 mt-1 flex-wrap">
        <span><span className="inline-block w-2 h-2 rounded-full bg-[#EF9F27] mr-1" />Packs ponctuels</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-[#1D9E75] mr-1" />Abonnements récurrents</span>
        <span className="text-zinc-600">Taille de bulle = nombre de clients · Le chiffre au centre = clients sur l'offre</span>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent = "#fff", hint }) {
  return (
    <div className="noam-card p-4">
      <div className="noam-overline">{label}</div>
      <div className="noam-display text-xl font-bold mt-1 truncate" style={{ color: accent }}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{hint}</div>}
    </div>
  );
}
